import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState } from 'react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useEnhancedAuth from '../EnhancedAuth';
import {
  apiConvertFarecard,
  apiLockFarecard,
  apiRemovePhysicalFarecard,
} from '../services/api';
import { Farecard } from '../services/types/apitypes';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import FarecardOptions from '../usecases/farecard/FarecardOptions';
import AppModal from '../components/AppModal';
import FarecardRemove from '../usecases/farecard/FarecardRemove';
import FarecardLock from '../usecases/farecard/FarecardLock';
import { cardsAllowCreateVirtual, cardsLoadAndSelect } from '../utils/cards';
import FarecardConvert from '../usecases/farecard/FarecardConvert';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const CardOptionsPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const { cardid } = useParams<{ cardid: string }>();

  const [selectedCard, setSelectedCard] = useState<Farecard | undefined>(
    undefined,
  );

  const [cards, setCards] = useState<Farecard[] | undefined>(undefined);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isLocking, setIsLocking] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    reloadCardData().then(() => console.log('Setting card'));
  }, [auth.isLoading]);

  const reloadCardData = async () => {
    await cardsLoadAndSelect(cardid, setCards, setSelectedCard, auth);
  };

  const addValue = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/value');
  };

  const history = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/history');
  };

  const registerToInstitution = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/institution');
  };


  const passes = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/passes');
  };

  const autoload = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/autoload');
  };

  const removeCard = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    setIsDeleting(true);
  };

  const convertCard = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    setIsConverting(true);
  };

  const lockCard = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    setIsLocking(true);
  };

  const cardInfo = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/information');
  };

   const farecapping = (f: Farecard) => {
    setSelectedCard(f);
    hideDialogs();
    router.push('/card/' + f.cardId + '/fares');
  };

  const hideDialogs = () => {
    setIsDeleting(false);
    setIsLocking(false);
    setIsConverting(false);
  };

  const returnToCard = () => {
    if (selectedCard) {
      router.push('/card/' + selectedCard.cardId + '/options');
    } else {
      returnToCards();
    }
  };

  const returnToCards = () => {
    router.push('/cards');
  };

  const removeCardFinish = async (card: Farecard) => {
    if (isSubmitting) {
      return;
    }

    try {
      hideDialogs();
      LoadingController.show();
      setIsSubmitting(true);
      await apiRemovePhysicalFarecard(card.cardId!, auth);
      await reloadCardData();
      present(getAppSuccess(t('farecards.removesuccess')));
      returnToCards();
    } catch (error) {
      console.error('Error removing card ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('farecards.removenosuccess')));
      }
      returnToCard();
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const lockCardFinish = async (card: Farecard) => {
    if (isSubmitting) {
      return;
    }

    try {
      hideDialogs();
      LoadingController.show();
      setIsSubmitting(true);
      await apiLockFarecard(card.cardId!, auth);
      await reloadCardData();
      present(getAppSuccess(t('farecards.locksuccess')));
      returnToCards();
    } catch (error) {
      console.error('Error locking card ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('farecards.locknosuccess')));
      }
      lockCard(card);
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const convertCardFinish = async (card: Farecard) => {
    if (isSubmitting) {
      return;
    }

    try {
      hideDialogs();
      LoadingController.show();
      setIsSubmitting(true);
      await apiConvertFarecard(card.cardId!, auth);
      await reloadCardData();
      present(getAppSuccess(t('farecards.convertsuccess')));
      returnToCards();
    } catch (error) {
      console.error('Error locking card ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('farecards.convertnosuccess')));
      }
      convertCard(card);
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  if (!selectedCard) {
    return <IonPage />;
  }

  const allowVirtual = () => {
    return cardsAllowCreateVirtual(cards);
  };

  return (
    <IonPage id="farecardvalue-page">
      <AppPageHeader isPageActive={isPageActive}
        back={() => returnToCards()}
        title={t('farecards.title')}
      ></AppPageHeader>
      <IonContent fullscreen className="ion-padding default-content">
        <FarecardOptions
          card={selectedCard}
          allowVirtual={allowVirtual()}
          addValue={addValue}
          history={history}
          passes={passes}
          autoload={autoload}
          remove={removeCard}
          convert={convertCard}
          lock={lockCard}
          cardInfo={cardInfo}
          farecapping={farecapping}
          registerToInstitution={registerToInstitution}
        ></FarecardOptions>
        <AppModal isOpen={isDeleting} close={() => setIsDeleting(false)}>
          <FarecardRemove
            card={selectedCard}
            remove={removeCardFinish}
            goBack={() => setIsDeleting(false)}
          ></FarecardRemove>
        </AppModal>
        <AppModal isOpen={isLocking} close={() => setIsLocking(false)}>
          <FarecardLock
            card={selectedCard}
            lock={lockCardFinish}
            goBack={() => setIsLocking(false)}
          ></FarecardLock>
        </AppModal>
        <AppModal isOpen={isConverting} close={() => setIsConverting(false)}>
          <FarecardConvert
            card={selectedCard}
            convert={convertCardFinish}
            goBack={() => setIsConverting(false)}
          ></FarecardConvert>
        </AppModal>
      </IonContent>
    </IonPage>
  );
};

export default CardOptionsPage;
